import type { Dictionary } from 'lodash';
import type { Driver } from '@bringg/types';
import { Notification } from '@bringg/react-components';
import type { GanttTimeLineItem } from '@bringg-frontend/gantt-timeline';
import type { RoutesPlannerBreak } from '../../components/routes-planner/routes-planner';
import type { NotificationsTranslation } from '../../components/routes-planner/translations';
import type { RoutesPlannerTask } from '../../types';
import { getDateToSort, splitAndPrioritySet, updateTaskAfterDrag } from './helpers';

export const updateTasksAfterItemMoved = ({
	movedItem,
	previousRunId,
	currentRunId,
	tasksInPreviousRun,
	tasksInCurrentRun,
	runToDriver,
	runToBreaks,
	linkedItem,
	notificationsTranslations,
	runToTasksInPresentedDate
}: {
	movedItem: GanttTimeLineItem;
	previousRunId: number;
	currentRunId: number;
	tasksInPreviousRun: RoutesPlannerTask[];
	tasksInCurrentRun: RoutesPlannerTask[];
	runToDriver: Dictionary<Driver>;
	runToBreaks: Dictionary<RoutesPlannerBreak[]>;
	linkedItem: GanttTimeLineItem;
	notificationsTranslations: NotificationsTranslation;
	runToTasksInPresentedDate: { [runId: number]: RoutesPlannerTask[] };
}): Dictionary<RoutesPlannerTask[]> => {
	if (currentRunId !== previousRunId) {
		const movedTask = tasksInPreviousRun.find(task => task.id === movedItem.id);
		const linkedTask =
			movedTask.linked_task_id && tasksInPreviousRun.find(task => task.id === movedTask.linked_task_id);

		const otherTasksInPreviousRun = tasksInPreviousRun.filter(
			task => task.id !== movedTask.id && task.id !== linkedTask?.id
		);

		const updatedTask = updateTaskAfterDrag(
			movedTask,
			movedItem,
			runToDriver[currentRunId],
			runToTasksInPresentedDate
		);

		let tasksInNewRun = [...tasksInCurrentRun, updatedTask];
		if (linkedTask) {
			linkedItem.rowId = movedItem.rowId;
			const updatedLinkedTask = updateTaskAfterDrag(
				linkedTask,
				linkedItem,
				runToDriver[currentRunId],
				runToTasksInPresentedDate
			);
			tasksInNewRun = [...tasksInNewRun, updatedLinkedTask];
			Notification.info(notificationsTranslations.moveLinkedOrder);
		}

		const [orderedTasksInPreviousRun] = updateRunPriorities(otherTasksInPreviousRun, runToBreaks[previousRunId]);
		const [orderedTasksInNewRun] = updateRunPriorities(tasksInNewRun, runToBreaks[currentRunId]);

		return {
			[previousRunId]: orderedTasksInPreviousRun,
			[currentRunId]: orderedTasksInNewRun
		};
	} else {
		const movedTaskIndex = tasksInCurrentRun.findIndex(task => task.id === movedItem.id);
		const movedTask = tasksInCurrentRun[movedTaskIndex];

		const updatedTask = updateTaskAfterDrag(
			movedTask,
			movedItem,
			runToDriver[currentRunId],
			runToTasksInPresentedDate
		);
		tasksInCurrentRun[movedTaskIndex] = updatedTask;

		const [sortedTasksInCurrentRun] = updateRunPriorities(tasksInCurrentRun, runToBreaks[currentRunId]);

		return { [currentRunId]: sortedTasksInCurrentRun };
	}
};

export const updateRunPriorities = (
	tasks: RoutesPlannerTask[],
	runBreaks: RoutesPlannerBreak[] = []
): [RoutesPlannerTask[], RoutesPlannerBreak[]] => {
	const tasksAndBreaks = [...tasks, ...runBreaks];
	const sortedTasksAndBreaks = sortTasksAndBreaksByDate(tasksAndBreaks);

	const [sortedTasks, sortedBreaks] = splitAndPrioritySet(sortedTasksAndBreaks);

	return [sortedTasks, sortedBreaks];
};

const sortTasksAndBreaksByDate = (tasksAndBreaks: (RoutesPlannerTask | RoutesPlannerBreak)[]) => {
	return tasksAndBreaks.sort((a, b) => {
		const dateA = getDateToSort(a);
		const dateB = getDateToSort(b);

		return dateA - dateB;
	});
};
